// Editable payload box for IMMEDIATE-ack approvals (fn-69 follow-up).
//
// **What is being approved: the tool INPUT.** A
// `custom_tool_result_immediate` gate is a driver-mediated custom tool —
// there is no server-side implementation, the human IS it. The agent
// proposes an input (`agent.custom_tool_use.input` → the row's
// `tool_args`), the operator confirms or amends it, and that object is
// posted straight back as the ack (`content: JSON.stringify(form_data)`).
// So the thing on screen is the agent's proposed INPUT, and the tool's
// own `input_schema` is its schema — by construction, not convention.
// There is no output to approve; the output happens after we say yes.
//
// **Nothing here is hard-coded per tool.** The tool definition already
// lives in the agent's pinned config: `config_snapshot.tools[]` is the
// VERBATIM authored array (`config-repo-resolver.ts` keeps the author
// shape for the snapshot and strips only what the MA wire rejects), and
// it is projected as `Agent.config` on every agent read. So the card
// reads the custom-tool entry for the gate's tool name and drives itself
// from the declared `description` + `input_schema`. A new custom tool is
// approvable — with its own description and validation — the day it is
// authored, with no console change.
//
// The CP is still authoritative: `form_data` must be a JSON object at
// the BFF (`input-helpers.ts`) and the CP runs any tool-specific check
// it has (e.g. `validateAskUserAnswers`). This validates client-side so
// a bad edit fails HERE, legibly, instead of round-tripping to a 400.

import { useState } from 'react';
import type React from 'react';

import './approvals.css';

import { bareToolName } from '../../headless/approval-policy.js';

/** The declared custom-tool entry from `config_snapshot.tools[]`. */
export interface CustomToolDef {
  readonly description?: string;
  readonly inputSchema?: Record<string, unknown>;
}

/**
 * Find the gate's custom-tool declaration in the agent's pinned config.
 * Matches on the BARE name so a federated tool still resolves. Returns
 * null when the config is absent/unparseable or declares no such tool —
 * the editor then falls back to the generic JSON-object check.
 */
export function findCustomToolDef(
  agentConfig: Record<string, unknown> | null,
  toolName: string,
): CustomToolDef | null {
  if (agentConfig === null || typeof agentConfig !== 'object') return null;
  const tools = (agentConfig as { tools?: unknown }).tools;
  if (!Array.isArray(tools)) return null;

  const candidates: Array<{ name: string; def: CustomToolDef }> = [];
  let exact: CustomToolDef | null = null;
  const wanted = bareToolName(toolName);
  for (const t of tools) {
    if (t === null || typeof t !== 'object') continue;
    const tt = t as {
      type?: unknown;
      name?: unknown;
      description?: unknown;
      input_schema?: unknown;
    };
    if (tt.type !== 'custom' || typeof tt.name !== 'string') continue;
    const def: { description?: string; inputSchema?: Record<string, unknown> } = {};
    if (typeof tt.description === 'string' && tt.description.length > 0) {
      def.description = tt.description;
    }
    // An array IS `typeof 'object'` — reject it, or a malformed
    // `input_schema: []` would silently pass as a schema that validates
    // nothing.
    if (
      tt.input_schema !== null &&
      typeof tt.input_schema === 'object' &&
      !Array.isArray(tt.input_schema)
    ) {
      def.inputSchema = tt.input_schema as Record<string, unknown>;
    }
    if (tt.name === toolName) exact = def;
    if (bareToolName(tt.name) === wanted) candidates.push({ name: tt.name, def });
  }

  // Exact name wins. Fall back to the bare name ONLY when it is
  // unambiguous — with both `alpha___review` and `beta___review`
  // declared, picking "whichever came first" would show one tool's
  // schema while approving the other's call.
  if (exact !== null) return exact;
  return candidates.length === 1 ? (candidates[0]?.def ?? null) : null;
}

/** Parsed-and-validated payload, or a human-readable reason why not. */
export type FormDataParse =
  | { readonly ok: true; readonly value: Record<string, unknown> }
  | { readonly ok: false; readonly error: string };

/**
 * Parse the editor text as the JSON object the BFF accepts
 * (`input-helpers.ts`: non-null, non-array object). Pure.
 */
export function parseFormDataJson(text: string): FormDataParse {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return { ok: false, error: 'Payload is empty — a JSON object is required.' };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch (err) {
    return {
      ok: false,
      error: `Invalid JSON: ${err instanceof Error ? err.message : 'parse failed'}`,
    };
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, error: 'Payload must be a JSON object (not an array, string or number).' };
  }
  return { ok: true, value: parsed as Record<string, unknown> };
}

/** The JSON type name of a parsed value, in JSON-Schema vocabulary. */
function jsonTypeOf(v: unknown): string {
  if (v === null) return 'null';
  if (Array.isArray(v)) return 'array';
  if (Number.isInteger(v)) return 'integer';
  return typeof v;
}

function typeMatches(value: unknown, declared: string): boolean {
  const actual = jsonTypeOf(value);
  if (declared === 'number') return actual === 'number' || actual === 'integer';
  if (declared === 'object') return actual === 'object';
  return actual === declared;
}

/**
 * Check an edited payload against the tool's declared `input_schema`.
 * Returns the first problem, or null when it looks right.
 *
 * ponytail: SHALLOW — top-level `required` keys + top-level property
 * `type` only. No nested objects, `enum`, `format`, `oneOf` or array
 * item types. It is a TYPO-CATCHER for the edits a human actually makes
 * (dropped a required field, turned an object into a string), NOT a
 * gate: `{"matterType":{}}` passes even when the schema requires
 * `matterType.jurisdiction`, and no server tier re-checks a generic
 * custom tool's payload (the CP validates only `ask_user_question`), so
 * a schema-invalid-but-shallow-valid object DOES reach the agent.
 * Upgrade path when that starts to bite: ajv, or a declared schema
 * check server-side.
 */
export function validateAgainstInputSchema(
  value: Record<string, unknown>,
  schema: Record<string, unknown> | undefined,
): string | null {
  if (schema === undefined) return null;
  // `Object.hasOwn`, never `in` — `in` walks the prototype chain, so an
  // optional property named `toString` would "exist" on `{}` and get
  // type-checked against `Object.prototype.toString`, and a required
  // `constructor` would read as present on any object.
  const required = (schema as { required?: unknown }).required;
  if (Array.isArray(required)) {
    for (const key of required) {
      if (typeof key !== 'string') continue;
      if (!Object.hasOwn(value, key) || value[key] === undefined) {
        return `Missing required field "${key}".`;
      }
    }
  }
  const properties = (schema as { properties?: unknown }).properties;
  if (properties !== null && typeof properties === 'object' && !Array.isArray(properties)) {
    for (const [key, rawProp] of Object.entries(properties as Record<string, unknown>)) {
      if (!Object.hasOwn(value, key) || rawProp === null || typeof rawProp !== 'object') continue;
      const declared = (rawProp as { type?: unknown }).type;
      if (typeof declared !== 'string') continue; // union / absent → skip
      if (!typeMatches(value[key], declared)) {
        return `Field "${key}" must be a ${declared} (got ${jsonTypeOf(value[key])}).`;
      }
    }
  }
  return null;
}

export interface ApprovalFormDataEditorProps {
  readonly toolName: string;
  /** The gate's `tool_args` — the agent's proposed input, and the prefill. */
  readonly toolArgs: Record<string, unknown>;
  /**
   * The agent's pinned `config_snapshot` (`Agent.config`), source of the
   * tool's declaration. Null where a surface has no agent row loaded —
   * the editor degrades to the generic JSON-object check.
   */
  readonly agentConfig?: Record<string, unknown> | null;
  /** Disables Approve (mutation in flight / row not resolved). */
  readonly busy: boolean;
  /** Approve-button copy; surfaces differ ("Approve and run" vs "Approve"). */
  readonly approveLabel: string;
  readonly onApprove: (formData: Record<string, unknown>) => void;
  /** Trailing controls rendered beside Approve (Deny, note input, links). */
  readonly children?: React.ReactNode;
}

export function ApprovalFormDataEditor(props: ApprovalFormDataEditorProps): React.ReactElement {
  const [text, setText] = useState(() => JSON.stringify(props.toolArgs, null, 2));
  const [error, setError] = useState<string | null>(null);
  const def = findCustomToolDef(props.agentConfig ?? null, props.toolName);

  const approve = (): void => {
    const parsed = parseFormDataJson(text);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    const schemaError = validateAgainstInputSchema(parsed.value, def?.inputSchema);
    if (schemaError !== null) {
      setError(schemaError);
      return;
    }
    setError(null);
    props.onApprove(parsed.value);
  };

  return (
    <div className="approval-form-data" data-testid="approval-form-data-editor">
      {def?.description !== undefined && (
        <p className="approval-form-data__desc" data-testid="approval-form-data-desc">
          {def.description}
        </p>
      )}
      <label className="approval-form-data__label">
        Tool input — edit before approving.
        <textarea
          className="approval-form-data__input"
          data-testid="approval-form-data-input"
          value={text}
          rows={Math.min(18, text.split('\n').length + 1)}
          spellCheck={false}
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      {error !== null && (
        <p
          className="approval-form-data__error"
          role="alert"
          data-testid="approval-form-data-error"
        >
          {error}
        </p>
      )}
      <div className="approval-form-data__actions">
        <button
          type="button"
          className="approval-form-data__approve btn btn--primary"
          onClick={approve}
          disabled={props.busy}
        >
          {props.approveLabel}
        </button>
        {props.children}
      </div>
    </div>
  );
}
